import { useFeatureValue } from "@growthbook/growthbook-react";
import ActionButton from "../common/ActionButton";
import CategoryBadge from "../common/CategoryBadge";
import type { Event } from "../../types/event";
import { growthbook } from "../../lib/growthbook";

type Props = {
    event: Event;
};

export default function EventSpotlight({ event }: Props) {
    const ctaLabel = useFeatureValue(
        "event-program-cta-copy",
        "Voir le programme local"
    );

    const handleProgramClick = () => {
        growthbook.logEvent("Program CTA Clicked", {
            button: "event_program_cta",
            ctaText: ctaLabel,
            eventSlug: event.slug,
            eventTitle: event.title,
            placement: "event_spotlight",
        });
    };

    return (
        <section className="overflow-hidden rounded-2xl border border-white/10 bg-[#0d121b]">
            <div className="grid lg:grid-cols-[0.85fr_1.15fr]">
                <div className="flex items-center justify-center bg-black/40 p-3 md:p-4">
                    <img
                        src={event.image}
                        alt={event.title}
                        className="h-auto max-h-[760px] w-full rounded-xl object-contain"
                    />
                </div>

                <div className="flex flex-col justify-center gap-6 p-6 md:p-8">
                    <CategoryBadge label="Événement vedette" />

                    <div>
                        <h3 className="text-3xl font-black uppercase leading-tight text-white md:text-4xl">
                            {event.title}
                        </h3>

                        <p className="mt-3 text-base leading-7 text-white/75">
                            {event.summary}
                        </p>

                        {event.description && (
                            <p className="mt-4 text-sm leading-7 text-white/65 md:text-base">
                                {event.description}
                            </p>
                        )}
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                                Date
                            </p>
                            <p className="mt-2 text-sm font-black uppercase text-white">
                                {event.dateLabel}
                            </p>
                        </div>

                        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                                Horaire
                            </p>
                            <p className="mt-2 text-sm font-black uppercase text-white">
                                {event.timeLabel}
                            </p>
                        </div>

                        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                                Lieu
                            </p>
                            <p className="mt-2 text-sm font-black uppercase text-white">
                                {event.location}
                            </p>
                        </div>
                    </div>

                    {event.program && event.program.length > 0 && (
                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                            <h4 className="text-lg font-black uppercase text-white">
                                Programme de la demi-finale
                            </h4>

                            <div className="mt-4 space-y-4">
                                {event.program.map((item) => (
                                    <div
                                        key={`${item.time}-${item.title}`}
                                        className="border-l-2 border-orange-400 pl-4"
                                    >
                                        <p className="text-sm font-black uppercase text-orange-300">
                                            {item.time}
                                        </p>

                                        <p className="mt-1 text-base font-bold text-white">
                                            {item.title}
                                        </p>

                                        {item.description && (
                                            <p className="mt-1 text-sm leading-6 text-white/60">
                                                {item.description}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <ActionButton
                            label={ctaLabel}
                            onClick={handleProgramClick}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}